import type { LogsEvent } from '@datadog/browser-logs';

const REDACTED = '[redacted]';
const NETWORK_STACK = 'network error';
const SCRUBBER_FAILURE = 'scrubber failure';
const MAX_TEXT_LENGTH = 4096;
const MAX_STACK_LENGTH = 16384;
const MAX_FRAME_LINE_LENGTH = 1024;

const NETWORK_ORIGIN: LogsEvent['origin'] = 'network';

const URL_SPLIT_PATTERN = /(\b(?:https?|wss?):\/\/[^\s"'`)]+)/i;
const WHOLE_URL_PATTERN = /^(?:https?|wss?):\/\/[^\s"'`)]+$/i;
const ASSET_NAME_SPLIT_PATTERN = /([\w.-]+\.(?:m?js|css|map|woff2?))(?!\w)/;
const URL_ORIGIN_PATTERN = /^[a-z][a-z\d+.-]*:\/\/[^/]*/i;
const PROMO_CODE_SEGMENT_PATTERN = /(\/codes\/)[^/]+/gi;
const IDENTIFIER_SEGMENT_PATTERN =
  /\/(?![^/]*\.(?:m?js|css|map|woff2?)(?:\/|$))(?:(?=[^/]*\d)[^/]{6,}|\p{Nd}{3,}(?=\/|$))/gu;
const EMAIL_PATTERN = /[A-Z0-9._%+-]{1,64}@[A-Z0-9.-]{1,255}\.[A-Z]{2,24}/gi;
const DOUBLE_QUOTED_PATTERN = /"(?:[^"\\]|\\.)*"/g;
const BACKTICK_QUOTED_PATTERN = /`(?:[^`\\]|\\.)*`/g;
const CURLY_QUOTED_PATTERN = /“[^”]*”|‘[^’]*’/g;
const SINGLE_QUOTED_PATTERN = /(^|[^A-Za-z])('(?:[^'\\]|\\.|'(?=[A-Za-z]))*')/g;
const MBI_PATTERN =
  /[1-9][AC-HJKMNP-RT-Y][AC-HJKMNP-RT-Y\d]\d-?[AC-HJKMNP-RT-Y][AC-HJKMNP-RT-Y\d]\d-?[AC-HJKMNP-RT-Y]{2}\d{2}/gi;
const DATE_PATTERN = /\b\d{1,4}[/.-]\d{1,2}[/.-]\d{1,4}\b/g;
const CODE_TOKEN_PATTERN = /\b(?=[A-Z]*\d)(?=\d*[A-Z])[A-Z\d]{6,}\b/gi;
const DIGITS_PATTERN = /\p{Nd}{3,}/gu;
const STATUS_PREFIX_PATTERN = /\bstatus(?: code)?:?\s$/i;
const STATUS_PREFIX_LOOKBEHIND = 12;
const STACK_FRAME_PATTERN =
  /^\s+at\s(?:.+\()?(?:[a-z][a-z\d+.-]*:\/\/|\/)\S+:\d+:\d+\)?\s*$|^\S*@(?:[a-z][a-z\d+.-]*:\/\/|\/)\S+:\d+:\d+$/i;
const FRAME_LOCATION_PATTERN = /((?:[a-z][a-z\d+.-]*:\/\/|\/)\S+?)(:\d+:\d+)(\)?\s*)$/i;

const REPORTED_STATUSES: ReadonlySet<LogsEvent['status']> = new Set([
  'error',
  'critical',
  'alert',
  'emerg',
]);
const EVENT_KEYS: ReadonlySet<string> = new Set([
  'date',
  'message',
  'status',
  'origin',
  'service',
  'session_id',
  'session',
  'view',
  'error',
  'http',
  'ddtags',
  '_dd',
]);
const FAILURE_KEYS: ReadonlySet<string> = new Set(['date', 'status', 'origin', 'service', 'ddtags']);
const VIEW_KEYS: ReadonlySet<string> = new Set(['url', 'referrer', 'id']);
const SESSION_KEYS: ReadonlySet<string> = new Set(['id']);
const ERROR_KEYS: ReadonlySet<string> = new Set([
  'kind',
  'message',
  'stack',
  'causes',
  'handling',
  'source_type',
]);
const CAUSE_KEYS: ReadonlySet<string> = new Set(['message', 'source', 'type', 'stack']);
const HTTP_KEYS: ReadonlySet<string> = new Set(['method', 'status_code', 'url']);
const DD_KEYS: ReadonlySet<string> = new Set(['debug_ids']);
const DEBUG_ID_KEYS: ReadonlySet<string> = new Set(['url', 'id']);

const keepOnly = (record: object, keys: ReadonlySet<string>): void => {
  for (const key of Object.keys(record)) {
    if (!keys.has(key)) delete (record as Record<string, unknown>)[key];
  }
};

export const stripUrl = (url: string): string => url.split(/[?#]/, 1)[0];

export const scrubUrl = (url: string): string => {
  const stripped = stripUrl(url);
  const origin = stripped.match(URL_ORIGIN_PATTERN)?.[0] ?? '';
  const path = stripped
    .slice(origin.length)
    .replace(PROMO_CODE_SEGMENT_PATTERN, `$1${REDACTED}`)
    .replace(IDENTIFIER_SEGMENT_PATTERN, `/${REDACTED}`);
  return (origin + path).replace(EMAIL_PATTERN, REDACTED).replace(MBI_PATTERN, REDACTED);
};

const redactQuoted = (quoted: string): string => {
  const inner = quoted.slice(1, -1);
  return WHOLE_URL_PATTERN.test(inner) ? `${quoted[0]}${scrubUrl(inner)}${quoted.slice(-1)}` : REDACTED;
};

const redactDigits = (digits: string, offset: number, whole: string): string =>
  digits.length === 3 &&
  STATUS_PREFIX_PATTERN.test(whole.slice(Math.max(0, offset - STATUS_PREFIX_LOOKBEHIND), offset))
    ? digits
    : REDACTED;

const redactIdentifiers = (text: string): string =>
  text
    .replace(MBI_PATTERN, REDACTED)
    .replace(DATE_PATTERN, REDACTED)
    .replace(CODE_TOKEN_PATTERN, REDACTED)
    .replace(DIGITS_PATTERN, redactDigits);

const redactPlainText = (text: string): string =>
  text
    .replace(EMAIL_PATTERN, REDACTED)
    .split(ASSET_NAME_SPLIT_PATTERN)
    .map((part, index) => (index % 2 === 1 ? part : redactIdentifiers(part)))
    .join('');

const redactTo = (text: string, limit: number): string =>
  text
    .replace(DOUBLE_QUOTED_PATTERN, redactQuoted)
    .replace(BACKTICK_QUOTED_PATTERN, redactQuoted)
    .replace(CURLY_QUOTED_PATTERN, REDACTED)
    .replace(SINGLE_QUOTED_PATTERN, (_match, prefix: string, quoted: string) => prefix + redactQuoted(quoted))
    .split(URL_SPLIT_PATTERN)
    .map((part, index) => (index % 2 === 1 ? scrubUrl(part) : redactPlainText(part)))
    .join('')
    .slice(0, limit);

export const redactText = (text: string): string => redactTo(text, MAX_TEXT_LENGTH);

const isStackFrame = (line: string): boolean =>
  line.length <= MAX_FRAME_LINE_LENGTH && STACK_FRAME_PATTERN.test(line);

const scrubFrame = (line: string): string =>
  line
    .replace(
      FRAME_LOCATION_PATTERN,
      (_match, url: string, location: string, tail: string) => `${scrubUrl(url)}${location}${tail}`,
    )
    .replace(EMAIL_PATTERN, REDACTED)
    .replace(MBI_PATTERN, REDACTED);

export const scrubStack = (stack: string): string => {
  const scrubbed: string[] = [];
  let pending: string[] = [];
  const flush = () => {
    if (pending.length) {
      scrubbed.push(redactTo(pending.join('\n'), MAX_STACK_LENGTH));
      pending = [];
    }
  };
  for (const line of stack.split('\n')) {
    if (isStackFrame(line)) {
      flush();
      scrubbed.push(scrubFrame(line));
    } else {
      pending.push(line);
    }
  }
  flush();
  return scrubbed.join('\n').slice(0, MAX_STACK_LENGTH);
};

const scrubEvent = (event: LogsEvent): void => {
  keepOnly(event, EVENT_KEYS);
  event.message = redactText(event.message);

  if (event.view) {
    keepOnly(event.view, VIEW_KEYS);
    event.view.url = scrubUrl(event.view.url ?? '');
    if (event.view.referrer) event.view.referrer = scrubUrl(event.view.referrer);
  }

  if (event.session) keepOnly(event.session, SESSION_KEYS);

  if (event.error) {
    keepOnly(event.error, ERROR_KEYS);
    if (event.error.message) event.error.message = redactText(event.error.message);
    if (event.origin === NETWORK_ORIGIN) event.error.stack = NETWORK_STACK;
    else if (event.error.stack) event.error.stack = scrubStack(event.error.stack);
    event.error.causes?.forEach((cause) => {
      keepOnly(cause, CAUSE_KEYS);
      cause.message = redactText(cause.message ?? '');
      if (cause.stack) cause.stack = scrubStack(cause.stack);
    });
  }

  if (event.http) {
    keepOnly(event.http, HTTP_KEYS);
    event.http.url = scrubUrl(event.http.url ?? '');
  }

  if (event._dd) {
    keepOnly(event._dd, DD_KEYS);
    event._dd.debug_ids?.forEach((entry) => {
      keepOnly(entry, DEBUG_ID_KEYS);
      entry.url = stripUrl(entry.url ?? '');
    });
  }
};

export const beforeSend = (event: LogsEvent): boolean => {
  try {
    if (!REPORTED_STATUSES.has(event.status)) return false;
    scrubEvent(event);
  } catch {
    keepOnly(event, FAILURE_KEYS);
    event.message = SCRUBBER_FAILURE;
  }
  return true;
};
